import { useCallback, useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Calculator, FlaskConical, Map as MapIcon, PieChart, Settings2, SlidersHorizontal, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../lib/api";
import { can, useAuth } from "../lib/auth";
import { MapaRiesgo } from "../components/riesgo/MapaRiesgo";
import { Portafolio } from "../components/riesgo/Portafolio";
import { Parametros } from "../components/riesgo/Parametros";
import { money } from "../lib/casePath";
import { Btn, Card } from "../components/kit/Kit";
import { UploadPanel } from "../components/poc/UploadPanel";
import { JobsPanel } from "../components/poc/JobsPanel";
import { CfdiSummary } from "../components/poc/CfdiSummary";
import { AmortizationCheck } from "../components/poc/AmortizationCheck";
import { AlertsPanel } from "../components/poc/AlertsPanel";
import { MemoPanel } from "../components/poc/MemoPanel";
import { MotorPanel } from "../components/poc/MotorPanel";

const useJob = (id, deps) => {
  const [job, setJob] = useState(null);
  useEffect(() => {
    if (!id) return setJob(null);
    http.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(null));
  }, [id, deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return job;
};
const inp = "mt-1 w-full rounded-xl border border-[#E2DDD5] bg-[#FAF8F5] px-3 py-2.5 text-sm outline-none focus:border-[#E8643C] focus:bg-white";
const lbl = "block text-[11px] font-semibold uppercase tracking-widest text-[#8C867A]";

export const Simulador = () => {
  const [t, setT] = useState({ recursos: 5000000, plazo: 24, tasa: 22, gracia: 3, bullet: 20 });
  const [r, setR] = useState(null);
  useEffect(() => {
    const h = setTimeout(() => http.post("/calc/amortization", { recursos: +t.recursos, plazo_meses: +t.plazo, tasa_anual: t.tasa / 100, meses_gracia: +t.gracia,
      pct_bullet: t.bullet / 100 }).then((x) => setR(x.data)).catch(() => setR(null)), 350);
    return () => clearTimeout(h);
  }, [t]);
  const filas = r?.tabla?.filas || [];
  return (
    <Card title="Simulador de crédito" subtitle="Ajusta las condiciones y ve la amortización y la TIR al instante, sin crear deal" testid="simulador">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[["recursos", "Monto (MXN)"], ["plazo", "Plazo (meses)"], ["tasa", "Tasa anual %"], ["gracia", "Meses de gracia"], ["bullet", "Bullet %"]].map(([k, l]) => (
          <label key={k} className={lbl}>{l}<input type="number" value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })} className={inp} data-testid={`sim-${k}`} /></label>))}
      </div>
      {!r ? <p className="mt-5 text-sm text-[#8C867A]">Revisa las condiciones capturadas.</p> : <>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="sim-kpis">
          {[["Pago en gracia", money(r.tabla.pago_gracia)], ["Pago mensual", money(r.tabla.pago_frances)], ["TIR nominal", `${(r.tir.nominal * 100).toFixed(2)}%`],
            ["TIR efectiva", `${(r.tir.efectiva * 100).toFixed(2)}%`]].map(([l, v]) => (
            <div key={l} className="rounded-2xl border border-[#E2DDD5] bg-white p-4"><p className={lbl}>{l}</p><p className="font-serif-display mt-1 text-xl font-bold" data-testid={`sim-kpi-${l}`}>{v}</p></div>))}
        </div>
        <div className="mt-5 h-56"><ResponsiveContainer width="100%" height={224} minWidth={200}>
          <AreaChart data={filas} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#F2F0EC" vertical={false} /><XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#8C867A" }} interval="preserveStartEnd" minTickGap={16} />
            <YAxis tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} tick={{ fontSize: 11, fill: "#8C867A" }} width={48} />
            <Tooltip formatter={(v) => money(v)} labelFormatter={(m) => `Mes ${m}`} />
            <Area type="monotone" dataKey="saldo_final" name="Saldo" stroke="#E8643C" fill="#FDF0EB" strokeWidth={2} isAnimationActive={false} />
          </AreaChart></ResponsiveContainer></div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3" data-testid="sim-fases">{(r.fases || []).map((f) => (
          <div key={f.fase} className="rounded-xl bg-[#FAF8F5] px-4 py-3 text-sm"><p className="font-semibold">{f.fase} · meses {f.meses}</p><p className="text-xs text-[#5C5850]">Pago {money(f.pago_max)} · intereses {money(f.intereses)}</p></div>))}</div>
      </>}
    </Card>
  );
};

export const Ajustes = () => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get("/settings/validaciones").then((r) => setD(r.data.dias)); }, []);
  const save = async () => { try { await http.put("/settings/validaciones", { dias: d }); toast.success("Vigencias guardadas"); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  return (
    <Card title="Vigencia de documentos" subtitle="Días máximos de antigüedad que valida Risk AI Engine" testid="ajustes-validaciones" right={<Settings2 size={16} className="text-[#8C867A]" />}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[["opinion_cumplimiento", "Opinión 32-D"], ["constancia_situacion_fiscal", "Constancia fiscal"], ["estado_cuenta", "Estados de cuenta"], ["estados_financieros", "Estados financieros"]].map(([k, l]) => (
          <label key={k} className={lbl}>{l}<input type="number" min={1} value={d[k]} onChange={(e) => setD({ ...d, [k]: Number(e.target.value) })} className={inp} data-testid={`vigencia-${k}`} /></label>))}
      </div>
      <Btn kind="accent" className="mt-4" onClick={save} data-testid="vigencia-guardar">Guardar vigencias</Btn>
    </Card>
  );
};

export default function RiskEngine() {
  const { user } = useAuth();
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "mapa");
  const TABS = [["mapa", "Mapa de riesgo", MapIcon], ["portafolio", "Portafolio", PieChart], ["parametros", "Parámetros", SlidersHorizontal], ["laboratorio", "Laboratorio", FlaskConical]];
  return (
    <main className="mx-auto max-w-7xl space-y-8 px-4 py-8 sm:px-6 lg:px-10" data-testid="risk-engine-page" style={{ animation: "rise .35s ease-out both" }}>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 rounded-full bg-[#111] px-3 py-1 text-xs font-semibold text-white"><Sparkles size={12} className="text-[#E8643C]" />Risk AI Engine</p>
          <h1 className="font-serif-display mt-3 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">Risk Engine<span className="text-[#E8643C]">.</span></h1>
          <p className="mt-3 text-base text-[#5C5850]">Riesgo regional con datos públicos, concentración del portafolio, parámetros institucionales y laboratorio de documentos y simulación.</p>
        </div>
        <div className="no-scrollbar flex gap-1 overflow-x-auto rounded-xl border border-[#E2DDD5] bg-white p-1" data-testid="risk-tabs">
          {TABS.filter(([k]) => k !== "parametros" || can(user, "risk.acceder")).map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`risk-tab-${k}`} className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-[#111] text-white" : "text-[#5C5850] hover:bg-[#F2F0EC]"}`}><I size={14} />{l}</button>))}
        </div>
      </div>
      <div key={tab} style={{ animation: "rise .25s ease-out both" }}>
        {tab === "mapa" && <MapaRiesgo />}
        {tab === "portafolio" && <Portafolio />}
        {tab === "parametros" && <Parametros />}
        {tab === "laboratorio" && <Laboratorio />}
      </div>
    </main>
  );
}

export function Laboratorio() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [memoId, setMemoId] = useState(null);
  const refresh = useCallback(() => http.get("/jobs").then((r) => setJobs(r.data)), []);
  const loadAlerts = useCallback(() => {
    setAlertsLoading(true);
    http.get("/poc/dataset").then((r) => setAlerts(r.data.alertas)).finally(() => setAlertsLoading(false));
  }, []);
  const active = jobs.some((j) => ["queued", "running"].includes(j.status));
  const docJobs = jobs.filter((j) => j.type !== "memo");
  const docsActive = docJobs.some((j) => ["queued", "running"].includes(j.status));
  const doneCount = docJobs.filter((j) => j.status === "done").length;
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(refresh, 1500);
    return () => clearInterval(t);
  }, [active, refresh]);
  useEffect(() => { if (!memoId) { const m = jobs.find((j) => j.type === "memo"); if (m) setMemoId(m.id); } }, [jobs]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!docsActive) loadAlerts(); }, [doneCount, docsActive, loadAlerts]);
  const cfdi = jobs.find((j) => j.status === "done" && j.type === "cfdi_zip") || jobs.find((j) => j.status === "done" && j.type === "cfdi_xml");
  const cfdiJob = useJob(cfdi?.id, cfdi?.id);
  const memoStatus = jobs.find((j) => j.id === memoId);
  const memoJob = useJob(memoId, memoStatus ? `${memoStatus.status}-${memoStatus.progress}` : null);
  const clear = async () => { await http.delete("/jobs"); setSelected(null); setMemoId(null); setAlerts([]); refresh(); toast.success("Documentos de prueba eliminados"); };
  const generate = async () => { const r = await http.post("/poc/memo", {}); setMemoId(r.data.job_id); refresh(); toast.success("Generando Credit Memo…"); };
  return (
    <div className="space-y-8" data-testid="risk-laboratorio">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-6"><UploadPanel onQueued={refresh} onClear={clear} /></div>
        <div className="lg:col-span-6"><JobsPanel jobs={jobs} selectedId={selected} onSelect={setSelected} /></div>
      </div>
      <AlertsPanel alerts={alerts} onRefresh={loadAlerts} loading={alertsLoading} />
      <CfdiSummary result={cfdiJob?.result} />
      <MemoPanel memoJob={memoJob || memoStatus} onGenerate={generate} disabled={docsActive || doneCount === 0} />
      <div className="flex items-center gap-2 pt-4"><Calculator size={18} className="text-[#E8643C]" /><h2 className="text-lg font-bold">Simulación y motor</h2></div>
      <Simulador />
      <MotorPanel />
      <AmortizationCheck />
      <Ajustes />
    </div>
  );
}
